import MemberStore from '../../../iam/MemberStore'
import Workspace from '../../../iam/models/Workspace'
import { AlertFactory, AlertState } from '../../../alerts/Alert'
import { action, computed, makeObservable, observable, override } from 'mobx'
import ModalViewModel from '../../../infra/ModalViewModel'
import { task } from 'mobx-task'
import { Id } from '../../../types/definitions'
import UserDataStore from '../../../iam/UserDataStore'
import { GetCurrentMember } from '../../../iam/SessionStore'
import { SearchableUser } from '../../../iam/models/SearchableUser'

type CapacityType = 'Global' | 'Workspace'

export class ManageUserCapacityViewModel extends ModalViewModel {
  @observable
  private _selectedUserIds: Set<Id> = new Set()

  @observable
  capacityType: CapacityType = 'Workspace'

  @observable
  workspaceCapacity = 0

  @observable
  globalCapacity = 0

  @observable
  workspace = Workspace.none

  @observable
  selectedUser: SearchableUser = SearchableUser.none

  constructor(
    private memberStore: MemberStore,
    private userDataStore: UserDataStore,
    private newAlert: AlertFactory,
    private getCurrentMember: GetCurrentMember,
    private callback?: () => void,
  ) {
    super()
    makeObservable(this)
  }

  @computed
  get selectedUserIds() {
    return Array.from(this._selectedUserIds)
  }

  @computed
  get isPlatformAdmin() {
    return this.getCurrentMember().user.platformAdmin
  }

  @action
  setSelectedUserIds(selectedUserIds: Set<Id>) {
    this._selectedUserIds = selectedUserIds
  }

  @action
  setSelectedWorkspace(workspace: Workspace) {
    this.workspace = workspace
    this.setCapacityType(
      this.workspace === Workspace.none ? 'Global' : 'Workspace',
    )
  }

  @action
  setCapacityType(type: CapacityType) {
    this.capacityType = type
  }

  @action
  setWorkspaceAmount(workspaceAmount: number) {
    this.workspaceCapacity = workspaceAmount
  }

  @action
  setGlobalAmount(globalAmount: number) {
    this.globalCapacity = globalAmount
  }

  @action
  setSelectedUser(user: SearchableUser) {
    this.selectedUser = user
    this.setSelectedUserIds(new Set([user.publicId]))
  }

  @action
  async showWithUser(user: SearchableUser, workspace: Workspace) {
    this.setSelectedUser(user)
    this.setSelectedWorkspace(workspace)
    await this.fetchUserCapacity()

    this.show()
  }

  open(selectedUserIds: Set<Id>, workspace: Workspace) {
    this.setSelectedUserIds(selectedUserIds)
    this.setSelectedWorkspace(workspace)

    this.show()
  }

  async fetchUserCapacity() {
    if (this.workspace.id) {
      const member = await this.memberStore.fetchByUserPublicId({
        workspace_id: Id.assert(this.workspace.id),
        user_public_id: Id.assert(this.selectedUser.publicId),
      })

      this.setWorkspaceAmount(member?.maxConcurrentProviderJobs ?? 0)
    }

    if (this.isPlatformAdmin) {
      const userData = await this.userDataStore.fetchByPublicId(
        this.selectedUser.publicId,
        true,
      )

      this.setGlobalAmount(userData.jobCapacity)
    }
  }

  async updateWorkspaceCapacity() {
    await Promise.all(
      this.selectedUserIds.map((userId) =>
        this.memberStore.update(
          {
            user_public_id: Id.assert(userId),
            workspace_id: Id.assert(this.workspace.id),
          },
          { max_concurrent_provider_jobs: this.workspaceCapacity },
        ),
      ),
    )
  }

  async updateGlobalCapacity() {
    await Promise.all(
      this.selectedUserIds.map((userId) =>
        this.userDataStore.update(userId, {
          job_capacity: this.globalCapacity,
        }),
      ),
    )
  }

  async updateCapacity() {
    if (this.capacityType === 'Workspace') {
      return this.updateWorkspaceCapacity()
    }

    return this.updateGlobalCapacity()
  }

  submit = task.resolved(async () => {
    const alert = this.newAlert()

    alert
      .withState(AlertState.Pending)
      .saying('Updating Capacity!')
      .autoDismiss()
      .show()

    try {
      await this.updateCapacity()

      alert
        .withState(AlertState.Success)
        .saying('Success!', 'Capacity updated successfully!')
        .autoDismiss()
        .show()

      this.callback?.()

      this.close()
    } catch (e) {
      console.error(e)

      alert.fromError(e).autoDismiss()
    }
  })

  @override
  close() {
    super.close()
    this.setGlobalAmount(0)
    this.setWorkspaceAmount(0)
    this.setSelectedUser(SearchableUser.none)
  }
}
